<script lang="ts" setup>
  import { computed, ref } from 'vue'
  import { storeToRefs } from 'pinia'
  import { useI18n } from 'vue-i18n'
  import SelectLocale from '@/components/SelectLocale.vue'
  import { track } from '@vercel/analytics'
  import { logout as logoutFromApi } from '@/plugins/api'
  import { useUserStore } from '@/store/user'
  import {
    QuestionMarkCircleIcon,
    Cog6ToothIcon,
    ArrowRightEndOnRectangleIcon,
    UserCircleIcon
  } from '@heroicons/vue/24/outline'
  import router from '@/router'

  const { t } = useI18n({ useScope: 'global' })
  const { displayName } = storeToRefs(useUserStore())
  const menu = ref<HTMLUListElement | null>(null)

  const initials = computed(() =>
    (displayName.value ?? '')
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map(word => word[0])
      .join('')
      .toUpperCase()
  )

  function close() {
    menu.value?.hidePopover()
  }

  async function logoutHandler() {
    close()
    track('logout')
    logoutFromApi()
    router.push({ name: 'login' })
  }
</script>

<template>
  <div data-tour="tour-trigger">
    <button
      id="account-button"
      class="btn btn-ghost btn-sm btn-circle lg:btn-md"
      popovertarget="account-menu"
      :aria-label="t('nav.account')"
      :title="displayName ?? t('nav.account')"
      data-tour="theme-toggle"
    >
      <span
        v-if="initials"
        class="bg-neutral text-neutral-content flex size-8 items-center justify-center rounded-full text-xs font-semibold"
        >{{ initials }}</span
      >
      <UserCircleIcon
        v-else
        class="size-7"
      />
    </button>
    <ul
      id="account-menu"
      ref="menu"
      class="dropdown dropdown-end nav-sheet menu rounded-box bg-base-100 w-60 text-sm normal-case shadow-sm"
      popover
    >
      <li>
        <RouterLink
          :to="{ name: 'about' }"
          @click="close"
        >
          <QuestionMarkCircleIcon class="size-5" />
          {{ t('nav.help') }}
        </RouterLink>
      </li>
      <li>
        <RouterLink
          :to="{ name: 'settings' }"
          @click="close"
        >
          <Cog6ToothIcon class="size-5" />
          {{ t('settings.title') }}
        </RouterLink>
      </li>
      <li>
        <SelectLocale />
      </li>
      <li class="border-base-300 mt-1 border-t pt-1">
        <a
          class="text-error"
          href="#"
          @click.prevent="logoutHandler"
        >
          <ArrowRightEndOnRectangleIcon class="size-5" />
          {{ t('auth.logout') }}
        </a>
      </li>
    </ul>
  </div>
</template>

<style lang="css" scoped>
  #account-button {
    anchor-name: --account-menu;
  }
  #account-menu {
    position-anchor: --account-menu;
  }
</style>
